import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Options configuring the AutoSaveController.
 */
export interface IAutoSaveControllerOptions<T> {
  initialData: T;
  onSave: (data: T) => Promise<void> | void;
  debounceMs?: number;
  enabled?: boolean;
  minHoldMs?: number;
  savedTimeoutMs?: number;
  isEqual?: (prev: T, next: T) => boolean;
  onStateChange?: (
    status: "idle" | "saving" | "saved" | "error",
    error: string | null,
    lastSavedAt: number | null
  ) => void;
}

/**
 * Core controller managing auto-save state machine, timer debouncing, and save coordination.
 */
export class AutoSaveController<T> {
  private status: "idle" | "saving" | "saved" | "error" = "idle";
  private error: string | null = null;
  private lastSavedAt: number | null = null;
  private lastSavedData: T;
  private pendingData: T;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private savedResetTimer: ReturnType<typeof setTimeout> | null = null;
  private isSaving: boolean = false;
  private options: IAutoSaveControllerOptions<T>;

  constructor(options: IAutoSaveControllerOptions<T>) {
    this.options = options;
    this.lastSavedData = options.initialData;
    this.pendingData = options.initialData;
  }

  public getStatus(): "idle" | "saving" | "saved" | "error" {
    return this.status;
  }

  public getError(): string | null {
    return this.error;
  }

  public getLastSavedAt(): number | null {
    return this.lastSavedAt;
  }

  public updateOptions(newOptions: Partial<IAutoSaveControllerOptions<T>>) {
    this.options = { ...this.options, ...newOptions };
  }

  public hasDataChanged(prev: T, next: T): boolean {
    if (this.options.isEqual) {
      return !this.options.isEqual(prev, next);
    }
    if (prev === next) return false;
    try {
      return JSON.stringify(prev) !== JSON.stringify(next);
    } catch {
      return true;
    }
  }

  private notify() {
    this.options.onStateChange?.(this.status, this.error, this.lastSavedAt);
  }

  public async performSave(dataToSave: T) {
    if (this.options.enabled === false) return;
    if (this.isSaving) return;

    if (this.savedResetTimer) {
      clearTimeout(this.savedResetTimer);
      this.savedResetTimer = null;
    }

    this.isSaving = true;
    this.status = "saving";
    this.error = null;
    this.notify();

    const startTime = Date.now();
    try {
      await Promise.resolve(this.options.onSave(dataToSave));
      this.lastSavedData = dataToSave;
      this.lastSavedAt = Date.now();

      // Ensure "saving" state stays active for a calm minimum duration to prevent rapid visual flashing
      const minHold = this.options.minHoldMs ?? 0;
      const elapsed = Date.now() - startTime;
      if (minHold > 0 && elapsed < minHold) {
        await new Promise((resolve) => setTimeout(resolve, minHold - elapsed));
      }

      this.status = "saved";
      this.notify();

      // Automatically transition from "saved" back to "idle" after calm delay
      const resetDelay = this.options.savedTimeoutMs ?? 0;
      if (resetDelay > 0) {
        this.savedResetTimer = setTimeout(() => {
          this.savedResetTimer = null;
          if (this.status === "saved") {
            this.status = "idle";
            this.notify();
          }
        }, resetDelay);
      }
    } catch (err: any) {
      this.error = err?.message || "Failed to save changes.";
      this.status = "error";
      this.notify();
    } finally {
      this.isSaving = false;
    }
  }

  public onDataChange(newData: T) {
    this.pendingData = newData;
    if (this.options.enabled === false) return;

    if (!this.hasDataChanged(this.lastSavedData, newData)) {
      return;
    }

    if (this.savedResetTimer) {
      clearTimeout(this.savedResetTimer);
      this.savedResetTimer = null;
    }

    if (this.timer) {
      clearTimeout(this.timer);
    }

    const debounceMs = this.options.debounceMs ?? 600;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.performSave(newData);
    }, debounceMs);
  }

  public async flush() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (this.options.enabled !== false && this.hasDataChanged(this.lastSavedData, this.pendingData)) {
      await this.performSave(this.pendingData);
    }
  }

  public destroy() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.savedResetTimer) {
      clearTimeout(this.savedResetTimer);
      this.savedResetTimer = null;
    }
  }
}

/**
 * Options configuring the useAutoSave hook.
 */
export interface IUseAutoSaveOptions<T> {
  /** The current form/entity data object. */
  data: T;
  /** Async or sync callback invoked to persist data to the backend. */
  onSave: (data: T) => Promise<void> | void;
  /** Milliseconds to debounce keystrokes. Default is 600ms. */
  debounceMs?: number;
  /** Whether auto-save is enabled (e.g. false for new items, true for existing items with edit rights). */
  enabled?: boolean;
  /** Minimum milliseconds to hold the 'saving' status before switching to 'saved' to prevent rapid visual double-flash. Default is 300ms. */
  minHoldMs?: number;
  /** Milliseconds after which a 'saved' status automatically transitions back to 'idle'. Default is 2500ms. */
  savedTimeoutMs?: number;
  /** Optional comparator to check if data has meaningfully changed before triggering save. */
  isEqual?: (prev: T, next: T) => boolean;
}

/**
 * Return state from the useAutoSave hook.
 */
export interface IUseAutoSaveResult {
  /** Current auto-save state. */
  status: "idle" | "saving" | "saved" | "error";
  /** Error message if auto-save failed. */
  error: string | null;
  /** Immediately flushes any pending debounced save (e.g. on blur or form unmount). */
  flush: () => Promise<void>;
  /** Timestamp of the last successful save. */
  lastSavedAt: number | null;
}

/**
 * Custom React hook that automatically saves form/entity fields to the backend
 * with debouncing for text input and immediate flushing for toggles/blur.
 */
export function useAutoSave<T>({
  data,
  onSave,
  debounceMs = 600,
  enabled = true,
  minHoldMs = 300,
  savedTimeoutMs = 2500,
  isEqual,
}: IUseAutoSaveOptions<T>): IUseAutoSaveResult {
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  const controllerRef = useRef<AutoSaveController<T> | null>(null);

  if (!controllerRef.current) {
    controllerRef.current = new AutoSaveController<T>({
      initialData: data,
      onSave,
      debounceMs,
      enabled,
      minHoldMs,
      savedTimeoutMs,
      isEqual,
      onStateChange: (newStatus, newError, newLastSavedAt) => {
        setStatus(newStatus);
        setError(newError);
        if (newLastSavedAt) setLastSavedAt(newLastSavedAt);
      },
    });
  }

  useEffect(() => {
    controllerRef.current?.updateOptions({
      onSave,
      debounceMs,
      enabled,
      minHoldMs,
      savedTimeoutMs,
      isEqual,
    });
  }, [onSave, debounceMs, enabled, minHoldMs, savedTimeoutMs, isEqual]);

  const isInitialMount = useRef<boolean>(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    controllerRef.current?.onDataChange(data);
  }, [data]);

  useEffect(() => {
    return () => {
      controllerRef.current?.destroy();
    };
  }, []);

  const flush = useCallback(async () => {
    await controllerRef.current?.flush();
  }, []);

  return {
    status,
    error,
    flush,
    lastSavedAt,
  };
}

